import { useQuery } from "@tanstack/react-query"
import { ThemeRoot } from "@ternary/ui"
import { type CSSProperties, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { trackConsentedCustomerPage } from "../analytics.js"
import { loadCustomerOrderCompletion } from "../api.js"
import { customerQueryKeys } from "../query-keys.js"
import { ConsentBanner } from "../signup/consent-banner.js"
import { NEUTRAL_BRAND } from "../signup/neutral-brand.js"
import { useThemeColor } from "../theme-color.js"

export function OrderPage() {
  const { t } = useTranslation()
  const { key } = useParams()
  const [copied, setCopied] = useState(false)
  const keyInput = useRef<HTMLInputElement>(null)
  const order = useQuery({
    queryKey: customerQueryKeys.order,
    queryFn: loadCustomerOrderCompletion,
  })
  useThemeColor(order.data?.branding.primaryColor)

  useEffect(() => {
    if (!order.data) {
      return
    }
    document.title = `Order - ${order.data.branding.name}`
    trackConsentedCustomerPage(order.data.analytics, order.data.conversionEvent)
  }, [order.data])

  // Before the order answers, the tenant is not known: the neutral brand, never Ternary's green.
  if (order.isPending) {
    return (
      <ThemeRoot brand={NEUTRAL_BRAND}>
        <div
          className="legacy-dashboard-loading"
          aria-busy="true"
          aria-label={t("Loading order")}
          role="status"
        >
          <span className="legacy-dashboard-spinner" />
        </div>
      </ThemeRoot>
    )
  }

  if (order.isError) {
    return (
      <ThemeRoot brand={NEUTRAL_BRAND}>
        <main className="legacy-order-page legacy-order-state">
          <section>
            <h1>{t("Order unavailable")}</h1>
            <p>{t("We could not load this order confirmation.")}</p>
            <button className="legacy-order-button" type="button" onClick={() => order.refetch()}>
              {t("Retry")}
            </button>
          </section>
        </main>
      </ThemeRoot>
    )
  }

  const { analytics, branding, conversionEvent } = order.data
  const copyKey = async () => {
    if (!key) {
      return
    }
    const copySelectedInput = () => {
      keyInput.current?.focus()
      keyInput.current?.select()
      try {
        return document.execCommand("copy")
      } catch {
        return false
      }
    }
    let didCopy = false
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(key)
        didCopy = true
      } else {
        didCopy = copySelectedInput()
      }
    } catch {
      didCopy = copySelectedInput()
    }
    if (didCopy) {
      setCopied(true)
    }
  }

  // The page and its cookie banner in one theme root, in the tenant's brand.
  return (
    <ThemeRoot brand={branding.primaryColor}>
      <main
        className="legacy-order-page"
        style={{ "--order-primary": branding.primaryColor } as CSSProperties}
      >
        <section className="legacy-order-complete">
          <h1>{t("Order Complete!")}</h1>
          {key ? (
            <>
              <p>
                {t(
                  "Your order has been completed, please copy your key and move onto the dashboard to bind your key.",
                )}
              </p>
              <h2>{t("Key")}:</h2>
              <input aria-label={t("Membership key")} readOnly ref={keyInput} value={key} />
              <button
                className="legacy-order-button legacy-order-copy"
                type="button"
                onClick={copyKey}
              >
                {copied ? t("Copied!") : t("Copy Key")}
              </button>
              {/* Clickable before the copy too, so a copy that fails never traps the member. */}
              <button
                aria-describedby={copied ? undefined : "order-dashboard-hint"}
                className="legacy-order-button"
                type="button"
                onClick={() => window.location.assign("/dashboard")}
              >
                {t("Dashboard")}
              </button>
              {copied ? null : (
                <p className="legacy-order-hint" id="order-dashboard-hint">
                  {t("Copy your key to continue")}
                </p>
              )}
            </>
          ) : (
            <>
              <p>
                {t(
                  "Your order has been completed, please move onto the dashboard to access the server and your account.",
                )}
              </p>
              <a className="legacy-order-button" href="/dashboard">
                {t("Dashboard")}
              </a>
            </>
          )}
        </section>
      </main>
      <ConsentBanner
        note={t("Declining does not affect your order.")}
        onChoice={(choice) => {
          if (choice.ads) trackConsentedCustomerPage(analytics, conversionEvent)
        }}
        privacyUrl="/privacy"
      />
    </ThemeRoot>
  )
}
